import type { GetProp, TableProps } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import type { ConfigConsumerProps } from 'antd/es/config-provider';
import { ConfigContext } from 'antd/es/config-provider';
import type { TableLocale } from 'antd/es/table/interface';
import { getColumnKey, getColumnPos } from 'antd/es/table/util';
import defaultLocale from 'antd/locale/en_US';
import { useContext } from 'react';

import FilterInputDropdown from '../FilterInputDropdown';
import type { ColumnsType } from '../index';

export type TableFilters = Parameters<GetProp<TableProps, 'onChange'>>[1];

interface FilterConfig<RecordType = AnyObject> {
  filters?: TableFilters;
  mergedColumns: ColumnsType<RecordType>;
  prefixCls?: string;
  dropdownPrefixCls?: string;
  locale?: TableLocale;
}

// TODO: incorporate all PaginatedFilteredTable filtering logic to the hook

const useFilters = <RecordType extends AnyObject = AnyObject>(props: FilterConfig<RecordType>) => {
  const { filters, prefixCls: customizePrefixCls, dropdownPrefixCls: customizeDropdownPrefixCls, locale } = props;

  const { locale: contextLocale = defaultLocale, getPrefixCls } = useContext<ConfigConsumerProps>(ConfigContext);

  const prefixCls = getPrefixCls('table', customizePrefixCls);
  const dropdownPrefixCls = getPrefixCls('dropdown', customizeDropdownPrefixCls);
  const tableLocale: TableLocale = { ...contextLocale.Table, ...locale };

  const transformColumns = (innerColumns: ColumnsType<RecordType>) =>
    innerColumns.map((column, index) => {
      let newColumn: ColumnsType<RecordType>[number] = column;
      if (newColumn.filterable) {
        const columnPos = getColumnPos(index);
        const columnKey = String(getColumnKey(newColumn, columnPos));

        newColumn = {
          ...newColumn,
          filterDropdown: (params) => (
            <FilterInputDropdown
              {...params}
              column={newColumn}
              prefixCls={`${prefixCls}-filter`}
              dropdownPrefixCls={dropdownPrefixCls}
              locale={tableLocale}
            />
          ),
          filterDropdownProps: {
            destroyOnHidden: true, // TODO: FilterDropdownProps.visible is not updated, so we destroy to detect the opening
          },
          defaultFilteredValue: filters?.[columnKey] || null,
          // TODO: use following as soon as all filter types are incorporated here
          // filtered:
          // filteredValue:
        };
      }

      return newColumn;
    });

  return [transformColumns];
};

export default useFilters;
